import React, { useState } from 'react';
import ReportShell from './ReportShell';
import useReport from './useReport';
import RankedBars from './charts/RankedBars';
import BuildOrderHeatmap from './BuildOrderHeatmap';
import AthleteJourney from './AthleteJourney';
import { ACTION_LABELS, SECTION_LABELS, labelFor } from './labels';

// Definitions sourced from backend/app/services/profile_completeness.py's
// SECTIONS semantics (see PLAN4A-OUTCOMES.md §3) -- the two sections whose
// plain-English name overstates what they actually measure. "measurables"
// reads as "the sport's full required set" but is really >=1 measurable;
// "competition_record" reads as "a season" but is really >=1 row in ANY
// ledger. Rendered as visible text, not a tooltip -- an owner skimming the
// table would otherwise assume the stronger meaning.
const SECTION_DEFINITIONS = {
  measurables:
    'at least one measurable recorded from the athlete’s sport catalog set '
    + '— not the sport’s full required set.',
  competition_record:
    'at least one row in any competition ledger (game stats, results, event '
    + 'marks, matches, or placements) — any single ledger counts.',
};

const toRows = (counts = {}) => Object.entries(counts).map(([key, value]) => ({
  key, value, label: labelFor(ACTION_LABELS, key),
}));

export default function ProfileBuildPathReport() {
  const [population, setPopulation] = useState('organic');
  const { data, loading, error } = useReport('profile-build', population);
  const path = data?.build_path || {};
  const withActions = path.athletes_with_actions ?? 0;
  const noActions = Math.max(0, (data?.population_size ?? 0) - withActions);

  return (
    <ReportShell
      title="Profile Completion & Build Path"
      headline={data?.headline
        ? data.headline.replace(/start with (\S+)\.$/, (_, k) => `start with “${labelFor(ACTION_LABELS, k)}”.`)
        : undefined}
      population={population}
      onPopulationChange={setPopulation}
      populationSize={data?.population_size ?? 0}
      thin={data?.thin}
      loading={loading}
      error={error}
    >
      {/* Build order leads the report (David, 2026-11-05). */}
      <h3>Build order</h3>
      <p className="report-note">
        Each row is an action; each column is how far into an athlete’s journey it
        first happened. Darker means more athletes. Percentages are of the
        {' '}{withActions} athletes with at least one recorded action. This, first
        actions and last new action below are <strong>all-time</strong> — not
        limited by the completeness snapshot day.
      </p>
      <BuildOrderHeatmap positions={path.position_counts} athletes={withActions} />

      <h3>Completion by section</h3>
      {/*
       * Section `rate` is NOT computed over `population_size` (the header
       * denominator above). The backend divides by `scored_athletes` --
       * the count of rows in the latest daily completeness snapshot, i.e.
       * one row per athlete PROFILE that has been scored so far. The
       * header count is one row per athlete USER matching the population
       * filter, whether or not a snapshot has scored them yet. The two
       * numbers differ (scored_athletes <= population_size) and must stay
       * visibly distinct -- do not "simplify" this by reusing
       * population_size here, that is the exact silent-denominator-swap
       * bug this line exists to prevent.
       */}
      {data?.snapshot_day ? (
        <p className="report-note">
          Section rates are of the <strong>{data.scored_athletes}</strong> athletes
          scored in the snapshot taken <strong>{data.snapshot_day}</strong>.
        </p>
      ) : (
        <p className="report-note">No completeness snapshot has run yet.</p>
      )}
      {data?.wall && (
        <p className="report-note">
          The wall is <strong>{labelFor(SECTION_LABELS, data.wall)}</strong> — the
          least-completed section.
        </p>
      )}
      <RankedBars
        rows={(data?.sections || []).map((s) => ({
          key: s.section,
          label: labelFor(SECTION_LABELS, s.section),
          value: s.rate,
          caption: `${Math.round(s.rate * 100)}% · ${s.complete} athletes`,
        }))}
        max={1}
      />
      <dl className="report-definitions">
        {Object.entries(SECTION_DEFINITIONS).map(([section, def]) => (
          <div key={section}>
            <dt>{labelFor(SECTION_LABELS, section)}</dt>
            <dd>{def}</dd>
          </div>
        ))}
      </dl>

      <h3>What athletes do first</h3>
      <p className="report-note">
        All-time, like the build order — not limited by the snapshot day above.
      </p>
      {noActions > 0 && (
        <p className="report-note">
          {noActions} of {data.population_size} athletes have no recorded profile action yet.
        </p>
      )}
      <RankedBars rows={toRows(path.first_action_counts)} />

      <h3>Last new thing they tried</h3>
      <p className="report-note">
        The most recent action each athlete did for the first time. This is not
        their last visit — an athlete who keeps editing their profile still shows
        under whichever new action they tried last.
      </p>
      <RankedBars rows={toRows(path.stop_points)} />

      {data?.payoff_note && <p className="report-note">{data.payoff_note}</p>}

      <h3>Athlete journey</h3>
      <AthleteJourney />
    </ReportShell>
  );
}
